import { useCallback, useEffect } from 'react';
import { MIDASProject } from '../../../../types/project.types';
import { PaneNode } from '../../../../types/pane.types';
import { createPaneId, createTabId } from '../../../../types';
import { useProjectStore } from '../../../stores/projectStore';
import { useLayoutStyleStore } from '../../../stores/layoutStyleStore';
import { createInitialLayout } from '../../../utils/layoutUtils';
import { initializeProjectState } from '../../../utils/projectUtils';
import { useAutoTabCreation } from '../../../hooks/useAutoTabCreation';
import logger from '../../../utils/logger';

interface UseProjectInitializationProps {
  project: MIDASProject | null;
  isLoading: boolean;
  layout: {
    rootPane: PaneNode | null;
    activeContainerId: string;
  };
  updateLayout: (layout: { rootPane: PaneNode | null; activeContainerId: string }) => void;
  activateTab: (containerId: string, tabId: string) => void;
}

/**
 * プロジェクト初期化に関するロジックを管理するカスタムフック
 * - ワークスペースの初期化
 * - レイアウトの復元
 * - アクティブタブの復元
 */
export function useProjectInitialization({
  project,
  isLoading,
  updateLayout
}: UseProjectInitializationProps) {

  // ワークスペースの初期化
  const initializeWorkspaces = useCallback((loadedProject: MIDASProject) => {

    // スタイルストアを初期化
    useLayoutStyleStore.getState().initializeStyles();

    // ワークスペースの初期化（projectStoreを使用）
    const projectStore = useProjectStore.getState();

    if (!loadedProject.workspaces || loadedProject.workspaces.length === 0) {
      // ワークスペースがない場合、デフォルトワークスペースを作成
      const newWorkspaceId = projectStore.createWorkspace({
        name: 'Main',
        layoutState: {
          rootPane: {
            type: 'container',
            id: createPaneId('default-container'),
            tabs: [],
            activeTabId: createTabId(''),
          },
          activeContainerId: 'default-container'
        }
      });

      // 新しいワークスペースをアクティブに
      projectStore.switchWorkspace(newWorkspaceId);
    } else if (!loadedProject.activeWorkspaceId) {
      // activeWorkspaceIdがない場合、最初のワークスペースをアクティブに
      projectStore.switchWorkspace(loadedProject.workspaces[0].id);
    }
  }, []);

  // プロジェクト状態からアクティブタブを復元
  const restoreActiveTabFromProjectState = useCallback(() => {
    // 現在はactiveTabの保存がないため、復元処理はスキップ
  }, []);

  // レイアウトの初期化
  const initializeLayout = useCallback((loadedProject: MIDASProject) => {

    // 現在のワークスペースを取得（projectStoreから）
    const projectStore = useProjectStore.getState();
    const activeWorkspace = projectStore.getCurrentWorkspace();

    if (activeWorkspace?.layoutState?.rootPane) {
      // ワークスペースのレイアウトを使用
      updateLayout({
        rootPane: activeWorkspace.layoutState.rootPane,
        activeContainerId: activeWorkspace.layoutState.activeContainerId || ''
      });

      // アクティブタブの復元
      if (activeWorkspace.layoutState.rootPane) {
        restoreActiveTabFromProjectState();
      }
    } else {
      // ワークスペースにレイアウトがない場合、デフォルトレイアウトを作成
      const initialLayout = createInitialLayout(loadedProject);
      updateLayout({
        rootPane: initialLayout,
        activeContainerId: initialLayout.id
      });

      // 現在のワークスペースにレイアウトを保存
      if (activeWorkspace) {
        projectStore.setLayout({
          rootPane: initialLayout,
          activeContainerId: initialLayout.id
        });
      }
    }
  }, [restoreActiveTabFromProjectState, updateLayout]);

  // プロジェクトの初期化とタブの自動作成
  useAutoTabCreation();

  // プロジェクトIDを保存（依存配列の最適化のため）
  const projectId = project?.id;

  // プロジェクトロード時の初期化
  // 注意: projectIdの変更時のみ初期化を実行（projectオブジェクトの更新では実行しない）
  useEffect(() => {
    if (isLoading || !project || !projectId) {
      return;
    }

    logger.info('[useProjectInitialization] プロジェクトの初期化を開始:', {
      projectId: project.id,
      name: project.metadata && 'name' in project.metadata ? project.metadata.name : 'Unknown'
    });

    // プロジェクトの初期化処理（戻り値は使用しない）
    initializeProjectState(project);

    // ワークスペースの初期化
    initializeWorkspaces(project);

    // レイアウトの初期化
    initializeLayout(project);

    logger.info('[useProjectInitialization] プロジェクトの初期化完了');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, isLoading]);

  return {
    initializeWorkspaces,
    restoreActiveTabFromProjectState,
    initializeLayout
  };
}
